import { ReactNode, useState } from "react";
import { faFilter } from "@fortawesome/pro-regular-svg-icons";

import { IPaginator } from "common/types";
import { Optional } from "common/utils/types";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";

import { ProductListFilterSurface } from "client/analytics/types";
import PillButton from "client/common/components/buttons/PillButton";

import {
  FilterChangeHandler,
  FilterDefinition,
  FilterValue,
  ProductSearchFilters,
  SelectedProductFilters,
} from "../types";
import AppliedFilterTags from "./AppliedFilterTags";
import FilterPanelModal from "./FilterPanelModal";
import SearchCount from "./SearchCount";

import styles from "../styles/ProductCardListHeader.module.less";

type ProductCardListHeaderProps = {
  paginator: IPaginator;
  isRefetching: boolean;
  /** Pluralized in the count, e.g. "deal" reads "Showing 1 - 14 of 14 deals". */
  entityName: string;
  definitions: FilterDefinition<SelectedProductFilters, FilterValue, ProductSearchFilters>[];
  filterOptions: Optional<ProductSearchFilters>;
  filters: SelectedProductFilters;
  changeFrom: (
    surface: ProductListFilterSurface,
  ) => FilterChangeHandler<SelectedProductFilters, ProductSearchFilters>;
  /** Whether the page offers filtering; false renders the result count, `aside` and `sort`. */
  showFilters?: boolean;
  /** Shown to the right of the count when the header has no filter controls of its own. */
  aside?: ReactNode;
  sort: ReactNode;
};

type FilterActionsProps = Pick<
  ProductCardListHeaderProps,
  "definitions" | "filterOptions" | "filters" | "changeFrom" | "sort"
>;

const FilterActions = ({
  definitions,
  filterOptions,
  filters,
  changeFrom,
  sort,
}: FilterActionsProps) => {
  const [isFiltering, setIsFiltering] = useState(false);

  return (
    <div className={styles.actions}>
      {filterOptions && (
        <div className={styles.chips}>
          <AppliedFilterTags<SelectedProductFilters, ProductSearchFilters>
            definitions={definitions}
            options={filterOptions}
            value={filters}
            onChange={changeFrom(ProductListFilterSurface.AppliedFilters)}
          />
        </div>
      )}
      <div className={styles.buttons}>
        <PillButton
          variant="secondary"
          size="s"
          type="button"
          onClick={() => setIsFiltering(true)}
          disabled={!filterOptions}
        >
          <span className={styles.filter}>
            <Icon icon={faFilter} /> Filter
          </span>
        </PillButton>
        {sort}
      </div>
      {filterOptions && (
        <FilterPanelModal
          definitions={definitions}
          options={filterOptions}
          isVisible={isFiltering}
          onClose={() => setIsFiltering(false)}
          value={filters}
          onChange={changeFrom(ProductListFilterSurface.Drawer)}
        />
      )}
    </div>
  );
};

/** The bar above a ProductCardList: result count, active filter chips, the filter modal, and the
 * sort. */
const ProductCardListHeader = ({
  paginator,
  isRefetching,
  entityName,
  definitions,
  filterOptions,
  filters,
  changeFrom,
  showFilters = true,
  aside,
  sort,
}: ProductCardListHeaderProps) => (
  <div className={styles.header}>
    <div className={styles.label} role="status">
      {isRefetching ? <Spinner /> : <SearchCount paginator={paginator} entityName={entityName} />}
    </div>
    {showFilters ? (
      <FilterActions
        definitions={definitions}
        filterOptions={filterOptions}
        filters={filters}
        changeFrom={changeFrom}
        sort={sort}
      />
    ) : (
      <>
        <div className={styles.aside}>{aside}</div>
        <div className={styles.sort}>{sort}</div>
      </>
    )}
  </div>
);

export default ProductCardListHeader;
